import { select, put, takeLatest } from 'redux-saga/effects';
import { makeSelectSettings } from 'containers/SettingsProvider/selectors';
import directions from 'utils/directions';
import {
  TRAVELMODE_STRAIGHT,
  TRAVELMODE_DONE,
} from 'utils/directions/constants';
import { getIndexed } from 'utils/arrays';
import simplify from 'utils/simplify';
import reverse from 'utils/geocode/reverse';
import parseAddress from './functions/parseAddress';
import needGeocode from './functions/needGeocode';
import { editRouting, editGeocode } from './actions';
import {
  EDIT_ADD,
  EDIT_DELETE,
  EDIT_INSERT,
  EDIT_MOVE,
  EDIT_REPLACE,
  EDIT_SAVE,
} from './constants';
import makeSelectEditService from './selectors';

const pendingLegs = {};

function needRouting(route) {
  return id => {
    if (pendingLegs[id] !== undefined) {
      return false;
    }
    const leg = route[id];
    return leg.routing !== TRAVELMODE_DONE;
  };
}

// Individual exports for testing
function* checkLegs() {
  const workingRoute = yield select(makeSelectEditService());
  const { legs } = workingRoute;
  if (legs.length <= 1) {
    // First leg is always dummy for starting point
    return;
  }
  const legIndex = legs.findIndex(needRouting(workingRoute));
  if (legIndex === -1) {
    return;
  }
  const leg1 = workingRoute[legs[legIndex]];
  const routingLegs = [leg1];
  const travelMode = leg1.routing;
  const points = [leg1.points[0], leg1.lastPoint];
  pendingLegs[leg1.id] = travelMode;
  if (
    legIndex < workingRoute.legs.length - 1 &&
    needRouting(workingRoute)(legs[legIndex + 1])
  ) {
    const leg2 = workingRoute[legs[legIndex + 1]];
    points.push(leg2.lastPoint);
    routingLegs.push(leg2);
    pendingLegs[leg2.id] = travelMode;
  }
  if (travelMode === TRAVELMODE_STRAIGHT) {
    yield put(editRouting(routingLegs, routingLegs.map(leg => leg.points)));
    return;
  }
  try {
    const fullRoutes = yield directions(points, travelMode);
    const routes = fullRoutes.map(simplify);
    const withPoints = routes.map((route, idx) => {
      const [start, end] = points.slice(idx, idx + 2);
      const result = [start, ...route, end];
      if (start.snap && start.snap.test(result.slice(0, 2))) {
        result.splice(0, 1);
      }
      if (end.snap && end.snap.test(result.slice(-2))) {
        result.splice(-1, 1);
      }
      return result;
    });
    yield put(editRouting(routingLegs, withPoints));
  } catch (err) {
    // TODO give proper error notification
    yield put(editRouting(routingLegs, routingLegs.map(leg => leg.points)));
  }
}

function* moveMap(action) {
  const autocenter = yield select(makeSelectSettings('autocenter')());
  if (!autocenter || !action.point || !action.point.snap) {
    return;
  }
  const { map } = action.point.snap;
  map.setView(action.point, undefined, { animate: true, duration: 1 });
}

function* reverseGeocode() {
  const workingRoute = yield select(makeSelectEditService());
  const locale = yield select(makeSelectSettings('locale')());
  const loc = getIndexed(workingRoute, 'legs', 0).points[0];
  if (!needGeocode(workingRoute.meta, loc)) {
    return;
  }
  const address = yield reverse(loc, locale);
  const result = parseAddress(address);
  yield put(editGeocode(result));
}

export default function* defaultSaga() {
  yield takeLatest(EDIT_ADD, checkLegs);
  yield takeLatest(EDIT_DELETE, checkLegs);
  yield takeLatest(EDIT_INSERT, checkLegs);
  yield takeLatest(EDIT_MOVE, checkLegs);
  yield takeLatest(EDIT_REPLACE, checkLegs);
  yield takeLatest(EDIT_ADD, moveMap);
  yield takeLatest(EDIT_INSERT, moveMap);
  yield takeLatest(EDIT_MOVE, moveMap);
  yield takeLatest(EDIT_SAVE, reverseGeocode);
}
